import { NgClass } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, HostListener, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Observable, of, Subscription, timer } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { CookieKeys, exposedRoutes, LANDING_PAGES } from './CONSTANTS/tpi-common-constants';
import { CompanyInfo } from './tpi-models/general/company-info';
import { TPISession } from './tpi-models/general/tpisession';
import { FooterComponent } from './tpi-components/footer/footer.component';
import { HeaderComponent } from './tpi-components/header/header.component';
import { NotificationModalComponent } from './tpi-components/modals/notification-modal/notification-modal.component';
import { LoggerService } from './tpi-services/logger/logger.service';
import { LoginLogoutService } from './tpi-services/login-logout/login-logout.service';
import { TpiIdleService } from './tpi-services/shared-services/tpi-idle.service';
import { SessionService } from './tpi-services/tpi-session/session.service';
import { SelectedCompanyStateService } from './tpi-services/tpi-statemanagement/selected-company-state.service';
import { UtilsService } from './tpi-services/utility/utils-service.service';
import { AccountInfo } from './tpi-models/general/account-info';

interface Window {
    FS: {
        identify: any;
        setUserVars: any;
    };
    location: {
        pathname: string;
    };
}
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    imports: [
        NgClass,
        RouterOutlet,
        HeaderComponent,
        FooterComponent,
        NotificationModalComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit, AfterViewChecked {
    account: AccountInfo;
    deepLink = false;
    hasSetFullStoryIdentifier = false;
    hideTitle = false;
    millisecond: number = null;
    resetEuaTokenTimerSub = new Subscription();
    session: TPISession;
    sessionExpireWarningModalShow = false;
    sessionIdleWarningModalShow = false;
    showNavigation = true;

    private cdr = inject(ChangeDetectorRef);
    private loggerService = inject(LoggerService);
    private loginLogoutService = inject(LoginLogoutService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private sessionService = inject(SessionService);
    private storeManagement = inject(SelectedCompanyStateService);
    private tpiIdleService = inject(TpiIdleService);
    private utilsService = inject(UtilsService);
    private window = inject<Window>(('window' as any));

    constructor() {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.route.firstChild.title.subscribe((title) => {
                this.loggerService.info(`Route Changed to: ${title}`);
            });
            this.route.firstChild.url.subscribe((url) => {
                this.loggerService.info(`Route url: /${url}`);
                if (!this.session && url[0].path !== 'stub') {
                    this.getSession();
                }
                this.utilsService.scrollToTopOfPage();
            });
            this.route.firstChild.data.subscribe((data) => {
                this.loggerService.info(`Display left-side nav: ${data['showNavigation']}`);
                this.showNavigation = data['showNavigation'];
                this.deepLink = data['deepLink'];
                this.hideTitle = data['hideTitle'] || false;
            });
        });
    }

    ngOnInit(): void {
        this.initialSession();
        this.loggerService.info('End TPI Initialization');
        if (this.window?.FS && !this.hasSetFullStoryIdentifier) {
            this.setFullStoryIdentifier();
        }
    }

    ngAfterViewChecked(): void {
        if (this.window?.FS && !this.hasSetFullStoryIdentifier) {
            this.setFullStoryIdentifier();
        }
    }

    @HostListener('window:beforeunload')
    leavingApp(): void {
        this.loggerService.info('Leaving TPI', this.session);
        this.loggerService.logCurrentContents();
    }

    checkIdle(): void {
        this.loggerService.info('Start idle timer: checkIdle()');
        this.tpiIdleService.onIdleEnd().subscribe(() => {
            this.reset();
        });
        this.tpiIdleService.onTimeoutWarning().subscribe(() => {
            this.loggerService.info('TPI EUA Idle Warning', {
                UserID: this.session.context.session.account.shortName,
                Company:
                    this.session.context.session.account.company.companyDescription,
                Role: this.session.context.session.account.role,
                Page: 'Policy-Search',
                Status: 'idle-warning'
            });
            this.loggerService.logCurrentContents();
            this.resetEuaTokenTimerSub.unsubscribe();
            this.sessionIdleWarningModalShow = true;
            this.cdr.detectChanges();
        });

        this.tpiIdleService.onTimeout().subscribe(() => {
            this.loggerService.info('TPI EUA Timed Out: start...', {
                UserID: this.session.context.session.account.shortName,
                Company:
                    this.session.context.session.account.company.companyDescription,
                Role: this.session.context.session.account.role.id,
                Page: 'Policy-Search',
                Status: 'idle-timeout'
            });
            this.loggerService.logCurrentContents();
            this.sessionService.deleteAllCookies();
            this.resetEuaTokenTimerSub.unsubscribe();
            this.sessionService.initializeSessionStorage('');
            this.tpiIdleLogout();
        });

        this.reset();
    }

    closeExpireModal(): void {
        this.loggerService.info('TPI EUA Expired: Clicked Keep working', {
            UserID: this.session.context.session.account.shortName,
            Company:
                this.session.context.session.account.company.companyDescription,
            Role: this.session.context.session.account.role,
            Page: 'Policy-Search',
            Status: 'Keep-Working'
        });
        this.loggerService.logCurrentContents();
        this.sessionExpireWarningModalShow = false;
    }

    closeIdleModal(): void {
        this.loggerService.info('TPI EUA Idle: Clicked Keep working', {
            UserID: this.session.context.session.account.shortName,
            Company:
                this.session.context.session.account.company.companyDescription,
            Role: this.session.context.session.account.role,
            Page: 'Policy-Search',
            Status: 'Keep-Working'
        });
        this.loggerService.logCurrentContents();
        this.resetEuaTokenTimerSub = this.resetEuaTokenTimer();
        this.sessionIdleWarningModalShow = false;
        this.reset();
    }

    getSession(): void {
        this.session = JSON.parse(sessionStorage.getItem('session')) as TPISession;
        if (this.session?.context?.session?.account) {
            const userName = this.session.context.session.account.shortName;
            const company = this.session.context.session.account.company;
            const companyName = company.companyDescription;
            this.account = this.session.context.session.account;
            this.storeCompany(company);
            sessionStorage.setItem('userName', userName);
            sessionStorage.setItem('companyName', companyName);
            sessionStorage.setItem('sessionId', this.sessionService.getCookie({ key: CookieKeys.SessionUUID })?.toString());
        }
    }

    initialSession(): void {
        const pathname = this.window.location.pathname;
        this.loggerService.info(`user requested access to path: ${pathname}; exposed paths: ${exposedRoutes}`);
        if (exposedRoutes.filter((path) => pathname.startsWith(path)).length !== 0) {
            if (pathname !== LANDING_PAGES.LANDING) {
                this.sessionService.deleteAllCookies();
                this.sessionService.initializeSessionStorage(null);
                return;
            }
        }

        this.millisecond = 1000;
        const euaTokenExpire = this.sessionService.getCookie({ key: CookieKeys.TPITokenExp });
        if (parseInt(euaTokenExpire, 10) <= Date.now() / this.millisecond) {
            this.sessionService.deleteAllCookies();
            this.tpiIdleLogout();
            return;
        }
        else {
            this.startSessionExpireWarningTimer();
            this.subscribeToSessionExpireEvents();
        }

        this.sessionService.loginSubject().subscribe({
            next: (isLoggedIn: boolean) => {
                if (isLoggedIn === false) {
                    this.sessionService.deleteAllCookies();
                    this.resetEuaTokenTimerSub.unsubscribe();
                    this.tpiIdleService.endUserSession(true);
                } else {
                    this.getSession();
                    this.checkIdle();
                    this.resetEuaTokenTimerSub = this.resetEuaTokenTimer();
                }
            }
        });
        this.redirectToChangePasswordOnPolicyAccessDeny();
    }

    redirectToChangePasswordOnPolicyAccessDeny(): void {
        const company: CompanyInfo = this.sessionService.getCompanyDetails();
        const autoAccess = company?.companyAutoAuth;
        const propertyAccess = company?.companyPropertyAuth;

        if (autoAccess === propertyAccess && autoAccess === 0) {
            this.sessionService.redirectToChangePassword();
        }
    }

    redirectToLogoutPage(): void {
        this.sessionExpireWarningModalShow = false;
        this.loggerService.info('TPI EUA idle warning logout', this.session);
        this.loggerService.logCurrentContents();
        this.loginLogoutService.logout();
    }

    reset(): void {
        this.tpiIdleService.startWatching();
    }

    startSessionExpireWarningTimer(): void {
        this.tpiIdleService.startSessionExpireWarningTimer();
    }

    storeCompany(company: CompanyInfo): void {
        this.storeManagement.updateSelectedCompanyFields({ company });
    }

    subscribeToSessionExpireEvents(): void {
        this.tpiIdleService.onSessionExpireWarning().subscribe(() => {
            this.sessionExpireWarningModalShow = true;
        });

        this.tpiIdleService.onSessionExpireTimeout().subscribe(() => {
            this.redirectToLogoutPage();
        });
    }

    tpiIdleLogout(): void {
        this.sessionIdleWarningModalShow = false;
        this.loggerService.info('tpiIdleLogout(): logout on idle ', this.session);
        this.loggerService.logCurrentContents();
        this.loginLogoutService.logout();
    }

    private resetEuaTokenTimer(): Subscription {
        const startTime = 0;
        const minutes = 1;
        const seconds = 5;
        const millisecond = 1000;
        const repeatInterval = minutes * seconds * millisecond;
        const delay = 180;
        return timer(startTime, repeatInterval).pipe(
            switchMap((): Observable<any> => {
                const euaExpire = parseInt(
                    this.sessionService.getCookie({ key: CookieKeys.TPITokenExp }),
                    10
                );
                const currentTimeStamp = Date.now() / this.millisecond;
                if (
                    currentTimeStamp >= euaExpire - delay &&
                    currentTimeStamp < euaExpire
                ) {
                    this.loggerService.info('Resetting Eua Token', {
                        UserID: this.session.context.session.account.shortName,
                        Company:
                            this.session.context.session.account.company.companyDescription,
                        Role: this.session.context.session.account.role,
                        Page: 'Auto',
                        Status: ''
                    });
                    return this.sessionService.resetEuaToken();
                }
                return of(null);
            })).subscribe();
    }

    private setFullStoryIdentifier(): void {
        let username = sessionStorage.getItem('userName');

        if (username) {
            username = username.toLowerCase();
            this.loggerService.info(`Updated full-story displayName to ${username}`, this.session);
            this.window.FS.identify(username, { displayName: username });
            this.hasSetFullStoryIdentifier = true;
        }
    }
}